import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import config, { SERVER_URL } from "@/config/config";
import { XpMarketSortType } from "@/constants/enum/xpMarket";
import {
  bidOnAuctionItemXPMarket,
  cancelListingXPMarket,
  finishAuctionItem,
  getSwapTokens,
  getXPMarketItem,
  listXP,
} from "@/helper/contractFunctions/XPMarket";
import { approveTokenWei } from "@/helper/contractFunctions";
import { XPMarketItemType } from "@/types/XPMarketItem";

export interface XPMarketState {
  isListXP: boolean;
  xpMarketSwapTokens: Array<{ address: string; price: number }>;
  xpMarketItems: XPMarketItemType[];
  isLoadingXPMarketList: boolean;
  isXpMarketAction: boolean;
  xpMarketSort: XpMarketSortType;
  xpMarketItem: XPMarketItemType | null;
}

const initialState: XPMarketState = {
  isListXP: false,
  xpMarketSwapTokens: [],
  xpMarketItems: [],
  isLoadingXPMarketList: false,
  isXpMarketAction: false,
  xpMarketSort: XpMarketSortType.XP,
  xpMarketItem: null,
};

export const listXPAction = createAsyncThunk(
  "xpMarket/listXPAction",
  async ({ xpType, listingToken, startPrice, duration, account }: {
    xpType: number;
    listingToken: string;
    startPrice: string;
    duration: number;
    account: string;
  }) => listXP(xpType, 1, listingToken, startPrice, duration * 3600, account)
);

export const getXPMarketSwapTokensAction = createAsyncThunk(
  "xpMarket/getXPMarketSwapTokensAction",
  getSwapTokens
);

export const getXPMarketListAction = createAsyncThunk(
  "xpMarket/getXPMarketListAction",
  async (_: { type: "all" | "my" | "history" }) => {
    const { data } = await axios.get(`${SERVER_URL}/xpmarket/items`);
    return data;
  }
);

export const cancelXPItemAction = createAsyncThunk(
  "xpMarket/cancelXPItemAction",
  async ({ itemId, account }: { itemId: number; account: string }) =>
    cancelListingXPMarket(itemId, account)
);

export const bidXPItemAction = createAsyncThunk(
  "xpMarket/bidXPItemAction",
  async ({ itemId, account, tokenId, nextBidPriceWei }: {
    itemId: number;
    account: string;
    tokenId: number;
    nextBidPriceWei: string;
  }) => bidOnAuctionItemXPMarket(itemId, tokenId, nextBidPriceWei, account)
);

export const finishXPItemAction = createAsyncThunk(
  "xpMarket/finishXPItemAction",
  async ({ itemId, account }: { itemId: number; account: string }) =>
    finishAuctionItem(itemId, account)
);

export const approveTokenXPMarketAction = createAsyncThunk(
  "xpMarket/approveTokenXPMarketAction",
  async ({ token, amountWei, account }: {
    token: string;
    amountWei: string;
    account: string;
  }) => approveTokenWei(token, amountWei, config.xpMarketAddress, account)
);

export const getXPMarketItemAction = createAsyncThunk(
  "xpMarket/getXPMarketItemAction",
  getXPMarketItem
);

const xpMarketSlice = createSlice({
  name: "xpMarket",
  initialState,
  reducers: {
    setXpMarketSort: (state, action: PayloadAction<XpMarketSortType>) => {
      state.xpMarketSort = action.payload;
    },
    setXPMarketItem: (state, action: PayloadAction<XPMarketItemType>) => {
      state.xpMarketItems = state.xpMarketItems.map((item) =>
        item.id === action.payload.id ? action.payload : item
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listXPAction.pending, (state) => {
        state.isListXP = true;
      })
      .addCase(listXPAction.fulfilled, (state) => {
        state.isListXP = false;
      })
      .addCase(listXPAction.rejected, (state, { error }) => {
        state.isListXP = false;
        console.log(error.message);
      })
      .addCase(getXPMarketSwapTokensAction.fulfilled, (state, { payload }) => {
        state.xpMarketSwapTokens = payload;
      })
      .addCase(getXPMarketSwapTokensAction.rejected, (_state, { error }) => {
        console.log("error", error.message);
      })
      .addCase(getXPMarketListAction.pending, (state) => {
        state.isLoadingXPMarketList = true;
      })
      .addCase(getXPMarketListAction.fulfilled, (state, { payload }) => {
        state.isLoadingXPMarketList = false;
        state.xpMarketItems = payload.map((item: any) => ({
          id: item.id,
          xpType: item.xpType,
          listingType: item.listingType,
          xpPoint: item.xpPoint,
          startingPrice: Number(item.startingPrice),
          currentPrice: Number(item.currentPrice),
          timestamp: item.timestamp,
          expireAt: item.expiresAt,
          token: item.token,
          seller: item.seller,
          buyer: item.buyer,
          status: item.status,
          txHash: item.txHash,
          bids: item.bids,
        })) as XPMarketItemType[];
      })
      .addCase(getXPMarketListAction.rejected, (state) => {
        state.isLoadingXPMarketList = false;
      });

    for (const action of [
      cancelXPItemAction,
      bidXPItemAction,
      finishXPItemAction,
      approveTokenXPMarketAction,
    ]) {
      builder
        .addCase(action.pending, (state) => {
          state.isXpMarketAction = true;
        })
        .addCase(action.fulfilled, (state) => {
          state.isXpMarketAction = false;
        })
        .addCase(action.rejected, (state, { error }) => {
          state.isXpMarketAction = false;
          console.log(error.message);
        });
    }

    builder
      .addCase(getXPMarketItemAction.pending, (state) => {
        state.xpMarketItem = null;
      })
      .addCase(getXPMarketItemAction.fulfilled, (state, { payload }) => {
        state.xpMarketItem = payload;
      })
      .addCase(getXPMarketItemAction.rejected, (_state, { error }) => {
        console.log(error.message);
      });
  },
});

export const { setXpMarketSort, setXPMarketItem } = xpMarketSlice.actions;
export default xpMarketSlice.reducer;
